<template>
  <div class="chat-container">
    <div class="chat-content">
      <!-- 空白对话时的欢迎页 -->
      <div class="welcome-screen" v-if="messages.length === 0">
        <div class="logo-container">
          <img src="@/assets/images/app.png" alt="AI助手" class="ai-logo">
        </div>
        <div class="welcome-text">
          <h3>欢迎使用营销AI小助手</h3>
          <p>我可以回答您营销相关的问题，内容涉及线上渠道运营、活动报名、价格管理、营销报表及数据工具使用等方面。请您输入您的问题或从下方选择常见问题开始对话。</p>
        </div>
      </div>

      <!-- 消息列表 -->
      <div class="chat-messages" ref="messagesContainer" v-show="messages.length > 0">
        <div v-for="(message, index) in messages" :key="index"
             :class="['message', message.role === 'user' ? 'user-message' : 'ai-message']">
          <div class="avatar" v-if="message.role === 'assistant'">
            <img src="@/assets/images/app.png" alt="AI" class="avatar-img">
          </div>
<!--          <div class="avatar" v-else>-->
<!--            <img src="@/assets/images/carrier.png" alt="User" class="avatar-img">-->
<!--          </div>-->

          <div class="message-bubble">
            <div class="message-content">
              <template v-if="message.role === 'user'">
                {{ message.content }}
              </template>
              <template v-else>
                <div class="message-content-wrapper">
                  <div v-html="message.renderedContent || renderMarkdown(message.content)"></div>
                  <div class="typing-dots"
                       v-if="index === messages.length - 1 && message.role === 'assistant' && loading">
                    <span></span><span></span><span></span>
                  </div>
                  <button
                      class="copy-button"
                      @click="copyMessage(message.content)"
                      title="复制内容"
                      v-if="message.role === 'assistant'"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor"
                            d="M16 1H4C2.9 1 2 1.9 2 3v14h2V3h12V1zm3 4H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                  </button>
                  <div class="copy-tip" v-if="showCopyTip">{{ copyTipText }}</div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-if="loading" class="loading-indicator">
          <div class="loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>

        <div class="messages-bottom-space"></div>
      </div>

      <div class="input-section">
        <div class="common-questions" v-if="commonQuestions.length > 0">
          <h3>常见问题</h3>
          <div class="questions-list">
            <div v-for="(question, index) in commonQuestions"
                 :key="index"
                 class="question-item"
                 :class="{ 'disabled': loading || isTyping }"
                 @click="!loading && !isTyping && selectCommonQuestion(question)">
              {{ question }}
            </div>
          </div>
        </div>

        <div class="chat-input">
          <form @submit.prevent="!loading && !isTyping && sendMessage()">
            <textarea
                v-model="userInput"
                placeholder="请输入您的问题，按Enter键提交..."
                :disabled="loading || isTyping"
                rows="3"
                @keydown.enter.exact.prevent="!loading && !isTyping && sendMessage()"
                @keydown.shift.enter.exact.prevent="userInput += '\n'"
                ref="chatInput"
            ></textarea>
            <button
                type="submit"
                class="send-button"
                :disabled="!userInput.trim() || loading || isTyping"
            >
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import { v4 as uuidv4 } from 'uuid'
import { commonApi } from '@/api'
import { streamPost } from '@/api/modules/common'
import deviceDetection from '@/mixins/deviceDetection'

export default {
  name: 'WebChat',
  mixins: [deviceDetection],
  data () {
    return {
      userInput: '',
      messages: [],
      loading: false,
      isSubmitting: false,
      isTyping: false,
      eventSource: null,
      sseTimeout: null,
      commonQuestions: [
        // '什么是人工智能？',
        // '如何使用这个聊天功能？',
        // '你能做什么？',
        // '如何联系客服？'
      ],
      messageUUId: '',
      marked: marked,
      showCopyTip: false,
      copyTipText: '',
      channel: ''
    }
  },
  methods: {
    renderMarkdown (content) {
      return this.marked(content)
    },
    async sendMessage () {
      if (this.eventSource) this.eventSource = null
      if (!this.userInput.trim() || this.loading || this.isTyping) return

      const userMessage = {
        role: 'user',
        content: this.userInput,
        time: this.formatTime(new Date())
      }

      this.messages.push(userMessage)
      const userQuery = this.userInput
      this.userInput = ''
      this.loading = true
      this.isSubmitting = true

      try {
        this.$nextTick(() => {
          this.safeScrollToBottom()
        })

        const aiMessage = {
          role: 'assistant',
          content: '',
          renderedContent: '',
          time: this.formatTime(new Date())
        }
        this.messages.push(aiMessage)

        const streamUrl = 'https://api.shinyotech.com/sse/admin/platform/chat/stream'
        this.eventSource = streamPost(streamUrl, {
          question: userQuery,
          uuid: this.messageUUId
        }, (e) => {
          const data = e
          const lastIndex = this.messages.length - 1
          if (lastIndex >= 0 && this.messages[lastIndex].role === 'assistant') {
            this.messages[lastIndex].content += data.content
            if (data.content === '[END]' || data.content === '[DONE]' ||
                data.content.includes('[END]') || data.content.includes('[DONE]')) {
              this.messages[lastIndex].content = this.messages[lastIndex].content
                .replace(/\[END\]|\[DONE\]/g, '').trim()
              if (this.eventSource) this.eventSource = null
              this.loading = false
              this.isSubmitting = false
            }

            // 更新当前消息的渲染内容
            this.messages[lastIndex].renderedContent = marked(this.messages[lastIndex].content)

            this.$nextTick(() => {
              this.safeScrollToBottom()
            })
          }
        }, (error) => {
          console.error('SSE错误:', error)
          if (this.eventSource) this.eventSource = null
          this.loading = false
          this.isSubmitting = false
        }, (data) => {
          console.log('Stream completed:', data)
          this.loading = false
          this.isSubmitting = false
        })
      } catch (error) {
        console.error('AI 回复出错:', error)
        this.messages.push({
          role: 'assistant',
          content: '抱歉，我遇到了一些问题。请稍后再试。',
          renderedContent: marked('抱歉，我遇到了一些问题。请稍后再试。'),
          time: this.formatTime(new Date())
        })
        this.loading = false
        this.isSubmitting = false
      }
    },
    formatTime (date) {
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${hours}:${minutes}`
    },
    safeScrollToBottom () {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },
    selectCommonQuestion (question) {
      if (this.loading || this.isTyping) return
      if (this.eventSource) this.eventSource = null

      this.userInput = question
      this.sendMessage()
    },
    async copyMessage (content) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(content)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = content
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          textArea.style.top = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()

          try {
            document.execCommand('copy')
          } catch (err) {
            console.error('复制失败:', err)
            throw err
          } finally {
            document.body.removeChild(textArea)
          }
        }

        this.showCopyTip = true
        this.copyTipText = '复制成功'
        setTimeout(() => {
          this.showCopyTip = false
        }, 2000)
      } catch (err) {
        this.showCopyTip = true
        this.copyTipText = '复制失败'
        setTimeout(() => {
          this.showCopyTip = false
        }, 2000)
        console.error('复制失败:', err)
      }
    },
    async init () {
      const data = {
        channel: ''
      }
      if (this.$route.query) {
        data.channel = this.$route.query.channel
      }
      commonApi.getQuestionList(data).then(data => {
        if (data.questionList.length > 0) {
          this.commonQuestions = data.questionList
        }
      })
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.safeScrollToBottom()
    })
  },
  beforeDestroy () {
    if (this.eventSource) this.eventSource = null
    if (this.messageUUId) this.messageUUId = ''
  },
  created () {
    this.messageUUId = uuidv4()
    this.init()
    const query = this.$route.query
    this.channel = query.channel
  }
}
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-width: 800px;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.chat-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  background-color: #f8fafc;
}

.welcome-screen {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 20px;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  max-height: calc(100vh - 200px); /* 减去输入区域和常见问题的高度 */
  overflow-y: auto;
}

.logo-container {
  margin-bottom: 16px;
  transform: scale(1);
  transition: transform 0.3s ease;
}

.logo-container:hover {
  transform: scale(1.05);
}

.ai-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.welcome-text {
  max-width: 500px;
  animation: fadeInUp 0.6s ease-out;
  margin-bottom: 16px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.welcome-text h3 {
  font-size: 20px;
  color: #2563eb;
  margin-bottom: 12px;
  font-weight: 600;
  letter-spacing: -0.3px;
}

.welcome-text p {
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  max-height: calc(100vh - 200px); /* 减去输入区域和常见问题的高度 */
}

.message {
  max-width: 70%;
  position: relative;
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  animation: messageSlide 0.3s ease-out;
}

@keyframes messageSlide {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-message {
  flex-direction: row-reverse;
  align-self: flex-end;
  margin-left: auto;
}

.user-message .message-bubble {
  background-color: #dcf8c6;
  color: #000000;
  border-bottom-right-radius: 4px;
}

.ai-message {
  margin-right: auto;
}

.ai-message .message-bubble {
  border-bottom-left-radius: 4px;
  background: #ffffff;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  margin-right: 10px;
  flex-shrink: 0;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.message-bubble {
  padding: 10px 14px;
  border-radius: 12px;
  position: relative;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: 14px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.03);
  line-height: 1.5;
}

.message-content {
  line-height: 1.5;
}

.message-content :deep(ul),
.message-content :deep(ol) {
  padding-left: 1.2em;
  margin: 0.4em 0;
}

.message-content :deep(li) {
  margin: 0.3em 0;
}

.message-content :deep(p) {
  margin: 0.4em 0;
  line-height: 1.5;
}

.message-content :deep(code) {
  background-color: rgba(0, 0, 0, 0.04);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.9em;
}

.message-content :deep(pre) {
  background-color: #f8fafc;
  padding: 10px;
  border-radius: 6px;
  margin: 0.6em 0;
  overflow-x: auto;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.message-content-wrapper {
  position: relative;
  padding-bottom: 32px;
}

.copy-button {
  position: absolute;
  right: 0;
  bottom: 4px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #64748b;
  opacity: 0;
  transition: all 0.2s ease;
  border-radius: 6px;
  font-size: 14px;
}

.message-content-wrapper:hover .copy-button {
  opacity: 0.7;
}

.copy-button:hover {
  opacity: 1 !important;
  color: #2563eb;
  background-color: rgba(37, 99, 235, 0.1);
}

.copy-tip {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 1000;
  animation: fadeInOut 2s ease-in-out;
}

.input-section {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background-color: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.02);
  z-index: 10;
}

.common-questions {
  margin-bottom: 16px;
}

.common-questions h3 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #475569;
  font-weight: 600;
}

.questions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.question-item {
  background-color: #f8fafc;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.question-item:hover:not(.disabled) {
  background-color: #2563eb;
  color: white;
  border-color: #2563eb;
  transform: translateY(-1px);
}

.question-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.chat-input {
  position: relative;
}

.chat-input form {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.chat-input textarea {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  font-size: 14px;
  resize: none;
  min-height: 44px;
  max-height: 120px;
  line-height: 1.5;
  transition: all 0.3s ease;
  font-family: inherit;
  background-color: #f8fafc;
}

.chat-input textarea:focus {
  border-color: #2563eb;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.send-button {
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 4px;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(37, 99, 235, 0.2);
}

.send-button:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
  box-shadow: none;
}

.send-button:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.typing-dots {
  display: inline-flex;
  gap: 4px;
  margin-left: 8px;
  vertical-align: middle;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  background-color: #2563eb;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

@media (max-width: 768px) {
  .chat-container {
    max-width: 100%;
    border-radius: 0;
  }

  .welcome-screen,
  .chat-messages {
    max-height: calc(100vh - 180px); /* 移动端稍微调整高度 */
  }

  .welcome-screen {
    padding: 16px;
    max-height: 45vh;
  }

  .ai-logo {
    width: 64px;
    height: 64px;
  }

  .welcome-text h3 {
    font-size: 18px;
    margin-bottom: 10px;
  }

  .welcome-text p {
    font-size: 13px;
  }

  .message {
    max-width: 80%;
  }

  .chat-input textarea {
    font-size: 14px;
    min-height: 40px;
  }
}
</style>
